import { useEffect, useState } from "react";
import { api, errDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { SectionHeader } from "@/components/SectionHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, KeyRound, Power, Building2, Copy, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Admin() {
  const { refreshCompanies } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetRequests, setResetRequests] = useState([]);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "member",
    company_ids: [],
  });

  const load = async () => {
    try {
      const [u, c, rr] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/companies"),
        api.get("/admin/password-reset-requests"),
      ]);
      setUsers(u.data);
      setCompanies(c.data);
      setResetRequests(rr.data);
    } catch (e) {
      console.warn("Admin load failed", e?.response?.status);
    }
  };

  const copyResetLink = async (token) => {
    const link = `${window.location.origin}/reset-password/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Reset link copied — share it privately");
    } catch {
      window.prompt("Copy this reset link:", link);
    }
  };

  const dismissReset = async (token) => {
    try {
      await api.delete(`/admin/password-reset-requests/${token}`);
      toast.success("Request dismissed");
      load();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;
    try {
      await api.post("/admin/companies", { name: newCompanyName.trim() });
      toast.success("Company created");
      setNewCompanyName("");
      setNewCompanyOpen(false);
      await load();
      await refreshCompanies();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const removeCompany = async (c) => {
    if (!window.confirm(`Delete "${c.name}" and ALL its data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/companies/${c.id}`);
      toast.success("Company removed");
      await load();
      await refreshCompanies();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.name) {
      toast.error("All fields required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setForm({ email: "", password: "", name: "", role: "member", company_ids: [] });
      setNewUserOpen(false);
      load();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User disabled" : "User enabled");
      load();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const openReset = (u) => {
    setResetUser(u);
    setNewPassword("");
    setResetOpen(true);
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/admin/users/${resetUser.id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset");
      setResetOpen(false);
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const toggleAssign = async (user, company) => {
    const isMember = user.company_ids?.includes(company.id);
    try {
      if (isMember) {
        await api.delete(`/admin/users/${user.id}/companies/${company.id}`);
      } else {
        await api.post(`/admin/users/${user.id}/companies/${company.id}`);
      }
      load();
    } catch (err) {
      toast.error(errDetail(err.response?.data?.detail));
    }
  };

  const toggleCompanyInForm = (cid) => {
    setForm((f) => ({
      ...f,
      company_ids: f.company_ids.includes(cid)
        ? f.company_ids.filter((x) => x !== cid)
        : [...f.company_ids, cid],
    }));
  };

  return (
    <div data-testid="admin-page">
      <SectionHeader
        overline="Control"
        title="Admin"
        description="Manage workspaces and who has access to them."
      />

      <Tabs defaultValue="users">
        <TabsList className="bg-[#F3F1ED] mb-6">
          <TabsTrigger value="users" data-testid="tab-users" className="data-[state=active]:bg-white">
            Users
          </TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies" className="data-[state=active]:bg-white">
            Companies
          </TabsTrigger>
          <TabsTrigger
            value="resets"
            data-testid="tab-resets"
            className="data-[state=active]:bg-white relative"
          >
            Reset requests
            {resetRequests.length > 0 && (
              <span
                data-testid="reset-badge"
                className="ml-2 bg-[#D26A59] text-white text-[10px] font-mono-num rounded-full px-1.5 py-0.5 leading-none"
              >
                {resetRequests.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="flex justify-end mb-4">
            <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="open-add-user"
                  className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                >
                  <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add user
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                    New user
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={createUser} className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Name</Label>
                    <Input
                      data-testid="user-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Email</Label>
                    <Input
                      data-testid="user-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Temp password</Label>
                    <Input
                      data-testid="user-password"
                      type="text"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="mt-1.5 font-mono-num"
                      placeholder="min 6 chars"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="user-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {companies.length > 0 && (
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">
                        Assign to companies
                      </Label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {companies.map((c) => {
                          const on = form.company_ids.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              type="button"
                              data-testid={`assign-${c.id}`}
                              onClick={() => toggleCompanyInForm(c.id)}
                              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                                on
                                  ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                                  : "bg-white border-[#EAE8E3] text-[#212421] hover:bg-[#F3F1ED]"
                              }`}
                            >
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewUserOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-user"
                      className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                    >
                      Create
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#EAE8E3] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Name</th>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Email</th>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Role</th>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Companies</th>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Status</th>
                  <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-right px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="users-table-body">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-t border-[#EAE8E3] hover:bg-[#FBFBF9]"
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="px-6 py-4 text-sm">{u.name}</td>
                    <td className="px-6 py-4 text-sm text-[#696E69] font-mono-num">{u.email}</td>
                    <td className="px-6 py-4 text-xs">
                      <span
                        className={`px-2 py-0.5 rounded-full uppercase tracking-wider ${
                          u.role === "admin"
                            ? "bg-[#1F4E3D]/10 text-[#1F4E3D]"
                            : "bg-[#F3F1ED] text-[#696E69]"
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {companies.map((c) => {
                          const on = u.company_ids?.includes(c.id);
                          return (
                            <button
                              key={c.id}
                              onClick={() => toggleAssign(u, c)}
                              data-testid={`toggle-${u.id}-${c.id}`}
                              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                on
                                  ? "bg-[#5C8065]/15 border-[#5C8065]/30 text-[#1F4E3D]"
                                  : "bg-white border-[#EAE8E3] text-[#696E69] hover:border-[#5C8065]/40"
                              }`}
                            >
                              {c.name}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      <span
                        className={`px-2 py-0.5 rounded-full ${
                          u.is_active
                            ? "bg-[#5C8065]/15 text-[#1F4E3D]"
                            : "bg-[#D26A59]/15 text-[#D26A59]"
                        }`}
                      >
                        {u.is_active ? "active" : "disabled"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openReset(u)}
                          data-testid={`reset-${u.id}`}
                          title="Reset password"
                          className="p-2 rounded-md hover:bg-[#F3F1ED] text-[#696E69]"
                        >
                          <KeyRound className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          data-testid={`toggle-active-${u.id}`}
                          title={u.is_active ? "Disable" : "Enable"}
                          className="p-2 rounded-md hover:bg-[#F3F1ED] text-[#696E69]"
                        >
                          <Power className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="companies">
          <div className="flex justify-end mb-4">
            <Dialog open={newCompanyOpen} onOpenChange={setNewCompanyOpen}>
              <DialogTrigger asChild>
                <Button
                  data-testid="open-add-company"
                  className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                >
                  <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> New company
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl tracking-tight font-medium">
                    New company
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={createCompany} className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">Name</Label>
                    <Input
                      data-testid="company-name"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      placeholder="e.g. Acme Corp, Personal"
                      className="mt-1.5"
                      autoFocus
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewCompanyOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="save-company"
                      className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
                    >
                      Create
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companies.map((c) => (
              <div
                key={c.id}
                className="bg-white border border-[#EAE8E3] rounded-lg p-6 flex items-start justify-between"
                data-testid={`company-card-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#5C8065]/12 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-[#1F4E3D]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display text-lg">{c.name}</div>
                    <div className="text-xs text-[#696E69] font-mono-num mt-0.5">
                      {c.member_count ?? 0} members
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => removeCompany(c)}
                  data-testid={`delete-company-${c.id}`}
                  className="p-2 rounded-md hover:bg-[#D26A59]/10 text-[#D26A59]"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="resets">
          {resetRequests.length === 0 ? (
            <div className="bg-white border border-[#EAE8E3] rounded-lg p-12 text-center text-sm text-[#696E69]">
              No pending reset requests.
            </div>
          ) : (
            <div className="bg-white border border-[#EAE8E3] rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Email</th>
                    <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Requested</th>
                    <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-left px-6 py-4">Expires</th>
                    <th className="text-xs tracking-[0.2em] uppercase font-bold text-[#696E69] text-right px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody data-testid="reset-requests-table-body">
                  {resetRequests.map((r) => (
                    <tr
                      key={r.token}
                      className="border-t border-[#EAE8E3] hover:bg-[#FBFBF9]"
                      data-testid={`reset-row-${r.id}`}
                    >
                      <td className="px-6 py-4 text-sm font-mono-num">{r.email}</td>
                      <td className="px-6 py-4 text-xs text-[#696E69] font-mono-num">
                        {new Date(r.created_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-4 text-xs text-[#696E69] font-mono-num">
                        {new Date(r.expires_at).toLocaleString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => copyResetLink(r.token)}
                            data-testid={`copy-link-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#1F4E3D] text-white text-xs hover:bg-[#163B2E] transition-colors"
                          >
                            <Copy className="w-3 h-3" strokeWidth={1.75} /> Copy link
                          </button>
                          <button
                            onClick={() => dismissReset(r.token)}
                            data-testid={`dismiss-${r.id}`}
                            title="Dismiss"
                            className="p-2 rounded-md hover:bg-[#F3F1ED] text-[#696E69]"
                          >
                            <MailCheck className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

      </Tabs>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight font-medium">
              Reset password
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <p className="text-sm text-[#696E69]">
              Setting a new password for <b>{resetUser?.email}</b>. Share it with them privately.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#696E69]">New password</Label>
              <Input
                data-testid="reset-password-input"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1.5 font-mono-num"
                placeholder="min 6 chars"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setResetOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                data-testid="save-reset-password"
                className="bg-[#1F4E3D] hover:bg-[#163B2E] text-white"
              >
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
